import styled from '@emotion/styled';
import { MapPin } from 'phosphor-react';
import { theme } from '../../styles/theme';

export interface Studio {
  studioID: string;
  name: string;
  image?: string;
  activities?: string[];
  location?: {
    street?: string;
    district?: string;
    city?: string;
  };
  distance?: number;
}

interface StudioCardProps {
  studio: Studio;
  onStudioClick?: (studioId: string) => void;
}

const Card = styled.div`
  background: var(--color-border-subtle);
  border: 1px solid var(--color-border-subtle);
  border-radius: ${theme.borderRadius.lg};
  width: 236px;
  height: 221px;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: transform ${theme.transitions.fast};

  &:active {
    transform: scale(0.98);
  }
`;

const ImageContainer = styled.div`
  background: #cecfd3;
  height: 109px;
  width: 100%;
  border-radius: ${theme.borderRadius.lg} ${theme.borderRadius.lg} 0 0;
  position: relative;
  overflow: hidden;
`;

const StudioImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImageGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 100%);
  pointer-events: none;
`;

const DistanceBadge = styled.div`
  position: absolute;
  right: 10px;
  top: 75px;
  background: rgba(0, 0, 0, 0.7);
  padding: 4px 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const DistanceText = styled.p`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: #FFFFFF;
  line-height: 16px;
  margin: 0;
`;

const Content = styled.div`
  padding: 15px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StudioName = styled.h3`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 15px;
  font-weight: ${theme.typography.fontWeight.medium};
  color: var(--color-text);
  line-height: 22px;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Location = styled.p`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 13px;
  font-weight: ${theme.typography.fontWeight.regular};
  color: var(--color-text-secondary);
  line-height: 22px;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TagsContainer = styled.div`
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  align-items: flex-start;
`;

const Tag = styled.div`
  background: var(--color-border-subtle);
  padding: 4px 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
`;

const TagText = styled.span`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.regular};
  color: var(--color-text-secondary);
  line-height: 14px;
  white-space: nowrap;
`;

const MapPinIcon = styled(MapPin)`
  width: 10px;
  height: 10px;
  color: #FFFFFF;
  flex-shrink: 0;
`;

export const StudioCard: React.FC<StudioCardProps> = ({ studio, onStudioClick }) => {
  const locationText = [
    studio.location?.district,
    studio.location?.city,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Card onClick={() => onStudioClick?.(studio.studioID)}>
      <ImageContainer>
        {studio.image ? (
          <>
            <StudioImage src={studio.image} alt={studio.name} />
            <ImageGradient />
          </>
        ) : (
          <ImageGradient />
        )}

        {studio.distance !== undefined && studio.distance !== null && (
          <DistanceBadge>
            <MapPinIcon weight="fill" />
            <DistanceText>{studio.distance.toFixed(1)} km</DistanceText>
          </DistanceBadge>
        )}
      </ImageContainer>

      <Content>
        <StudioName>{studio.name}</StudioName>

        {locationText && <Location>{locationText}</Location>}

        {studio.activities && studio.activities.length > 0 && (
          <TagsContainer>
            {studio.activities.slice(0, 2).map((activity, index) => (
              <Tag key={index}>
                <TagText>{activity}</TagText>
              </Tag>
            ))}
          </TagsContainer>
        )}
      </Content>
    </Card>
  );
};
